import React, { useLayoutEffect, useRef, useState, useEffect, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useRecoilValue } from 'recoil';
import { Control, Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Stack, Typography, MenuItem, Chip, Card, CardContent, Autocomplete, IconButton } from '@mui/material';
import { Add, Info, Settings, Delete } from '@mui/icons-material';
import Drawer from '../../../shared/Drawer';
import TextField from '../../../shared/TextField';
import CountryPickerLite from '../../../shared/CountryPickerLite';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { LokaliseField } from '../../../shared/LokaliseFields';
import { useData } from '../../../../data-layer';
import { useLocales } from '../../../../hooks';
import { usePermissions } from '../../../../hooks/Permissions/usePermissions';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { PermissionsGroupId } from '../../../../API';
import { HomepageOptions } from '../../../../state/theme';
import Button from '../../../shared/Button';
import {
  PlanPickerBody,
  PlanPickerResponse,
  TargetPlatform,
  PricingDurationType,
  TierLayoutBody,
  PlanTierResponse,
  PaymentPlansApi
} from '../../../../API';
import { apiConfig } from '../../../../utils/apiConfig';
import { markAsRequired } from '../../../../utils/formHelpers';
import { useHandleError } from '../../../../hooks/General/useHandleError';
import { planPickerFormTestIds } from '../../../shared/TestsIds';
import AssetBrowser from '../../../Assets/AssetBrowser';
import { AssetTypesEnum } from '../../../../utils/assetTypes';

const useStyles = makeStyles()((theme) => ({
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    padding: theme.spacing(6),
    maxWidth: '1200px',
    margin: '0 auto'
  },
  basicInfoSection: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4)
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(1),
    fontWeight: 600
  },
  formRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3)
  },
  countryPicker: {
    width: '35%'
  },
  platformsPicker: {
    width: '65%'
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    gap: theme.spacing(0.5)
  },
  sectionDescription: {
    color: theme.palette.text.secondary
  },
  pricingSection: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4)
  },
  pricingCard: {
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    position: 'relative',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#fafafa',
    '&:hover': {
      transform: 'translateY(-2px)'
    }
  },
  tierLayoutCard: {
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    position: 'relative',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f5f5f5'
  },
  paymentMethodsCard: {
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    position: 'relative',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: '1.1rem'
  },
  addButton: {
    textTransform: 'none',
    fontWeight: 500,
    width: 'fit-content',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)'
    }
  },
  arrayItem: {
    // Legacy compatibility
    position: 'relative'
  },
  footerButton: {
    minWidth: 120
  },
  durationTypePicker: {
    width: '50%'
  },
  durationPreselectedPicker: {
    width: '50%'
  },
  tierLayoutSection: {
    marginTop: theme.spacing(6)
  },
  tierLayoutContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  },
  tierTypePicker: {
    width: '50%'
  },
  defaultSkuPicker: {
    width: '50%'
  },
  paymentMethodTypePicker: {
    width: '50%'
  },
  paymentMethodsSection: {
    marginTop: theme.spacing(6)
  }
}));

const targetPlatformOptions = Object.values(TargetPlatform).map((platform) => ({
  value: platform,
  label: platform.charAt(0).toUpperCase() + platform.slice(1)
}));

const pricingDurationOptions = Object.values(PricingDurationType).map((duration) => ({
  value: duration,
  label: duration.charAt(0).toUpperCase() + duration.slice(1)
}));

export function PlanPickerForm(): React.ReactElement {
  const { classes } = useStyles();
  const { planPicker, plans } = useData();
  const { t } = useLocales();

  const formMetadata = useRecoilValue(planPicker.state.withFormMetadata);
  const isSaving = useRecoilValue(planPicker.state.withIsSaving);
  const planPickerData = formMetadata.record;
  const formRef = useRef<ValidatorForm>(null);

  // Plans data for SKU selection
  const allPlans = useRecoilValue(plans.state.withAllRecords);
  const planCodes = allPlans?.map((plan) => plan.code) || [];

  const methods = useForm<PlanPickerBody | PlanPickerResponse>();
  const { control, handleSubmit, reset } = methods;

  const {
    fields: pricingFields,
    remove: removePricing,
    append: appendPricing
  } = useFieldArray({
    control,
    name: 'pricing'
  });

  const { canSave } = usePermissionsGuard({
    homepageOption: HomepageOptions.MONETIZATION
  });
  const { hasUpsertPermission } = usePermissions();

  useLayoutEffect(() => {
    reset(planPickerData);
  }, [planPickerData, reset]);

  const onClose = () => {
    planPicker.hook.closeForm();
  };

  const onSubmit = async (planPickerToSave: PlanPickerBody | PlanPickerResponse) => {
    const savedPlanPicker = await planPicker.hook.save(planPickerToSave);
    if (savedPlanPicker) {
      onClose();
    }
  };

  const footerLeft = (
    <div>
      <Button
        className={classes.footerButton}
        color="primary"
        loading={isSaving}
        onClick={handleSubmit(onSubmit)}
        disabled={!hasUpsertPermission(PermissionsGroupId.MONETIZATION) && !canSave}
        data-testid={planPickerFormTestIds.saveButton}
      >
        {t('general.save')}
      </Button>
    </div>
  );

  const footerRight = (
    <Button
      className={classes.footerButton}
      color="grey"
      onClick={onClose}
      data-testid={planPickerFormTestIds.cancelButton}
    >
      {t('general.cancel')}
    </Button>
  );

  const addPricingConfiguration = () => {
    appendPricing({
      duration: {
        type: PricingDurationType.MONTHLY,
        title: '',
        preselected: false
      },
      layout: []
    });
  };

  return (
    <Drawer
      open={formMetadata.isShowingForm}
      onClose={onClose}
      headerLeft={
        <Stack direction="row" gap={4}>
          <Typography variant="h6">{t(`payments.${formMetadata.isEditing ? 'edit' : 'new'}_plan_picker`)}</Typography>
        </Stack>
      }
      footerLeft={footerLeft}
      footerRight={footerRight}
      formRef={formRef}
    >
      {planPickerData && (
        <div className={classes.formBody}>
          <FormProvider {...methods}>
            {/* Basic Information Section */}
            <Card className={classes.basicInfoSection} elevation={2} variant="outlined">
              <div className={classes.sectionHeader}>
                <Info />
                <Typography variant="h6" component="div">
                  {t('payments.plan_picker.basic_information')}
                </Typography>
              </div>

              <div className={classes.formRow}>
                <LokaliseField
                  name="title"
                  control={control}
                  toTranslate="payments.plan_picker"
                  dataTestId={planPickerFormTestIds.titleField}
                  required
                />
                <LokaliseField
                  name="stepTitle"
                  control={control}
                  toTranslate="payments.plan_picker"
                  dataTestId={planPickerFormTestIds.titleField}
                />
              </div>

              <div className={classes.formRow}>
                <Controller
                  control={control}
                  name="country"
                  rules={{ required: t('general.field_is_required') }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <CountryPickerLite
                      required
                      singleSelection
                      hidePresets
                      validateOnSubmit
                      value={value ? [value] : []}
                      onChange={(countries) => onChange(countries[0] || '')}
                      label={markAsRequired(t('payments.plan_picker.country'))}
                      error={error}
                      textFieldTestId={planPickerFormTestIds.countryField}
                      fullWidth
                      className={classes.countryPicker}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="platforms"
                  rules={{ required: t('general.field_is_required') }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <TextField
                      required
                      select
                      SelectProps={{ multiple: true }}
                      value={value || []}
                      onChange={(e) => onChange(e.target.value)}
                      label={t('payments.plan_picker.platforms')}
                      fieldError={error}
                      data-testid={planPickerFormTestIds.platformsField}
                      className={classes.platformsPicker}
                    >
                      {targetPlatformOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </div>
            </Card>

            {/* Pricing Configurations Section */}
            <Card className={classes.pricingSection} elevation={2} variant="outlined">
              <div className={classes.sectionHeader}>
                <Settings />
                <Typography variant="h6" component="div">
                  {t('payments.plan_picker.pricing_configurations')}
                </Typography>
              </div>

              {pricingFields.map((field, index) => (
                <Card key={field.id} className={classes.pricingCard} elevation={2} variant="outlined">
                  <CardContent>
                    <div className={classes.cardHeader}>
                      <div className={classes.cardTitle}>
                        <Chip
                          label={t('payments.plan_picker.pricing_configuration_index', { index: index + 1 })}
                          color="primary"
                          variant="filled"
                        />
                      </div>
                      <IconButton size="small" onClick={() => removePricing(index)} color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    </div>

                    <div className={classes.formRow}>
                      <Controller
                        control={control}
                        name={`pricing.${index}.duration.type`}
                        render={({ field: { value, onChange } }) => (
                          <TextField
                            select
                            value={value || PricingDurationType.MONTHLY}
                            onChange={(e) => onChange(e.target.value)}
                            label={t('payments.plan_picker.duration_type')}
                            className={classes.durationTypePicker}
                          >
                            {pricingDurationOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                      <LokaliseField
                        name={`pricing.${index}.duration.title`}
                        control={control}
                        label={t('payments.plan_picker.duration_title')}
                        dataTestId={`duration-title-${index}`}
                      />
                    </div>
                    <div className={classes.formRow}>
                      <Controller
                        control={control}
                        name={`pricing.${index}.duration.selectedTierPackageBackgroundImage`}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                          <AssetBrowser
                            assetType={AssetTypesEnum.PLAN_PICKER_PACKAGE_BACKGROUND}
                            value={value}
                            onChange={onChange}
                            label={t('payments.plan_picker.selected_tier_package_background_image')}
                            hasError={!!error}
                          />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`pricing.${index}.duration.preselected`}
                        render={({ field: { value, onChange } }) => (
                          <TextField
                            select
                            value={value ? 'true' : 'false'}
                            onChange={(e) => onChange(e.target.value === 'true')}
                            label={t('payments.plan_picker.preselected')}
                            className={classes.durationPreselectedPicker}
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </TextField>
                        )}
                      />
                    </div>
                    {/* Tier Layout Section */}
                    <TierLayoutSection pricingIndex={index} control={control} planCodes={planCodes} />
                  </CardContent>
                </Card>
              ))}

              <Button
                startIcon={<Add />}
                onClick={addPricingConfiguration}
                className={classes.addButton}
                variant="contained"
              >
                {t('payments.plan_picker.add_pricing')}
              </Button>
            </Card>
          </FormProvider>
        </div>
      )}
    </Drawer>
  );
}

// Tier Layout Section Component
interface TierLayoutSectionProps {
  pricingIndex: number;
  control: Control<PlanPickerResponse | PlanPickerBody, unknown>;
  planCodes?: string[];
}

function TierLayoutSection({ pricingIndex, control, planCodes }: TierLayoutSectionProps): React.ReactElement {
  const { classes } = useStyles();
  const { t } = useLocales();

  // Tiers data for tier type selection - moved from parent component
  const [tiers, setTiers] = useState<PlanTierResponse[]>([]);
  const [tiersLoading, setTiersLoading] = useState(false);

  // Memoize the API instance to prevent re-creation on every render
  const plansApi = useMemo(() => new PaymentPlansApi(apiConfig), []);
  const { handleError } = useHandleError();

  const {
    fields: tierFields,
    remove: removeTier,
    append: appendTier
  } = useFieldArray({
    control,
    name: `pricing.${pricingIndex}.layout`
  });

  // Fetch tiers on component mount only
  useEffect(() => {
    const fetchTiers = async () => {
      setTiersLoading(true);
      try {
        const { data: response } = await plansApi.getPlanTiers();
        if (response.body) {
          setTiers([...response.body, { name: 'package', adsAllow: false, rank: 0 }].sort((a, b) => a.rank - b.rank));
        }
      } catch (err) {
        console.error(err);
        handleError(err, t('errors.payments.get_tiers'), []);
      } finally {
        setTiersLoading(false);
      }
    };

    fetchTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const addTierLayout = () => {
    appendTier({
      title: '',
      stepTitle: '',
      type: '',
      defaultSku: '',
      badge: '',
      ctaText: '',
      layout: {
        title: '',
        footer: '',
        stepTitle: '',
        layout: []
      }
    } as TierLayoutBody);
  };

  return (
    <div className={classes.tierLayoutSection}>
      <Typography variant="subtitle1" className={classes.sectionTitle}>
        {t('payments.plan_picker.tier_layouts')}
        <Typography variant="caption" className={classes.sectionDescription}>
          {t('payments.plan_picker.tier_layouts_description')}
        </Typography>
      </Typography>
      <Card key={`tier-layout-${pricingIndex}`} className={classes.tierLayoutCard} elevation={2} variant="outlined">
        <CardContent>
          <div className={classes.tierLayoutContent}>
            {tierFields.map((tierField, tierIndex) => (
              <div key={tierField.id} className={classes.arrayItem}>
                <div className={classes.cardHeader}>
                  <div className={classes.cardTitle}>
                    <Chip
                      label={t('payments.plan_picker.tier_index', { index: tierIndex + 1 })}
                      color="primary"
                      variant="filled"
                    />
                  </div>
                  <IconButton size="small" onClick={() => removeTier(tierIndex)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </div>

                <div className={classes.formRow}>
                  {/* Tier Type */}
                  <Controller
                    control={control}
                    name={`pricing.${pricingIndex}.layout.${tierIndex}.type`}
                    rules={{ required: t('general.field_is_required') }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <TextField
                        required
                        select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        label={t('payments.plan_picker.tier_type')}
                        placeholder={t('payments.plan_picker.tier_type_placeholder')}
                        className={classes.tierTypePicker}
                        error={!!error}
                        disabled={tiersLoading}
                        helperText={tiersLoading ? t('general.loading') : error?.message}
                      >
                        {tiers.map((tier) => (
                          <MenuItem key={tier.name} value={tier.name}>
                            {tier.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />

                  {/* Default SKU */}
                  <Controller
                    control={control}
                    name={`pricing.${pricingIndex}.layout.${tierIndex}.defaultSku`}
                    rules={{ required: t('general.field_is_required') }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <Autocomplete
                        options={planCodes || []}
                        getOptionLabel={(code) => code}
                        value={value || null}
                        onChange={(_, newValue) => {
                          onChange(newValue || '');
                        }}
                        className={classes.defaultSkuPicker}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('payments.plan_picker.default_sku')}
                            placeholder={t('payments.plan_picker.default_sku_placeholder')}
                            required
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                        renderOption={(props, code) => <li {...props}>{code}</li>}
                      />
                    )}
                  />
                </div>

                {/* Localized Fields */}
                <LokaliseField
                  name={`pricing.${pricingIndex}.layout.${tierIndex}.title`}
                  control={control}
                  label={t('payments.plan_picker.tier_title')}
                  dataTestId={`tier-title-${pricingIndex}-${tierIndex}`}
                  required
                />

                <LokaliseField
                  name={`pricing.${pricingIndex}.layout.${tierIndex}.stepTitle`}
                  control={control}
                  label={t('payments.plan_picker.tier_step_title')}
                  dataTestId={`tier-step-title-${pricingIndex}-${tierIndex}`}
                  required
                />

                <LokaliseField
                  name={`pricing.${pricingIndex}.layout.${tierIndex}.badge`}
                  control={control}
                  required
                  label={t('payments.plan_picker.badge')}
                  dataTestId={`tier-badge-${pricingIndex}-${tierIndex}`}
                />

                <LokaliseField
                  name={`pricing.${pricingIndex}.layout.${tierIndex}.ctaText`}
                  control={control}
                  required
                  label={t('payments.plan_picker.cta_text')}
                  dataTestId={`tier-cta-text-${pricingIndex}-${tierIndex}`}
                />

                {/* Asset Fields */}
                <Controller
                  control={control}
                  name={`pricing.${pricingIndex}.layout.${tierIndex}.icon`}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <AssetBrowser
                      assetType={AssetTypesEnum.PLAN_PICKER_TIER_ICON}
                      value={value}
                      onChange={onChange}
                      label={t('payments.plan_picker.tier_icon')}
                      hasError={!!error}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`pricing.${pricingIndex}.layout.${tierIndex}.vixLogo`}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <AssetBrowser
                      assetType={AssetTypesEnum.PLAN_PICKER_VIX_LOGO}
                      value={value}
                      onChange={onChange}
                      label={t('payments.plan_picker.vix_logo')}
                      hasError={!!error}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`pricing.${pricingIndex}.layout.${tierIndex}.backgroundImage`}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <AssetBrowser
                      assetType={AssetTypesEnum.PLAN_PICKER_TIER_BACKGROUND}
                      value={value}
                      onChange={onChange}
                      label={t('payments.plan_picker.background_image')}
                      hasError={!!error}
                    />
                  )}
                />

                {/* Payment Methods Layout Section */}
                <PaymentMethodsLayoutSection pricingIndex={pricingIndex} tierIndex={tierIndex} control={control} />
              </div>
            ))}

            <Button
              startIcon={<Add />}
              onClick={addTierLayout}
              className={classes.addButton}
              variant="contained"
              size="small"
            >
              {t('payments.plan_picker.add_tier')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Payment Methods Layout Section Component
interface PaymentMethodsLayoutSectionProps {
  pricingIndex: number;
  tierIndex: number;
  control: Control<PlanPickerResponse | PlanPickerBody, unknown>;
}

function PaymentMethodsLayoutSection({
  pricingIndex,
  tierIndex,
  control
}: PaymentMethodsLayoutSectionProps): React.ReactElement {
  const { classes } = useStyles();
  const { t } = useLocales();

  const {
    fields: paymentMethodFields,
    remove: removePaymentMethod,
    append: appendPaymentMethod
  } = useFieldArray({
    control,
    name: `pricing.${pricingIndex}.layout.${tierIndex}.layout.layout`
  });

  const addPaymentMethod = () => {
    appendPaymentMethod({
      title: '',
      type: '',
      icon: ''
    });
  };

  return (
    <div className={classes.paymentMethodsSection}>
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        {t('payments.plan_picker.payment_methods')}
      </Typography>

      {/* Payment Methods Layout Metadata */}
      <LokaliseField
        name={`pricing.${pricingIndex}.layout.${tierIndex}.layout.title`}
        control={control}
        label={t('payments.plan_picker.payment_methods_title')}
        dataTestId={`payment-methods-title-${pricingIndex}-${tierIndex}`}
        required
      />

      <LokaliseField
        name={`pricing.${pricingIndex}.layout.${tierIndex}.layout.footer`}
        control={control}
        label={t('payments.plan_picker.payment_methods_footer')}
        dataTestId={`payment-methods-footer-${pricingIndex}-${tierIndex}`}
        required
      />

      <LokaliseField
        name={`pricing.${pricingIndex}.layout.${tierIndex}.layout.stepTitle`}
        control={control}
        label={t('payments.plan_picker.payment_methods_step_title')}
        dataTestId={`payment-methods-step-title-${pricingIndex}-${tierIndex}`}
        required
      />

      <Typography variant="subtitle2" className={classes.sectionTitle}>
        {t('payments.plan_picker.payment_methods_options')}
      </Typography>

      <Card
        key={`payment-methods-${pricingIndex}`}
        className={classes.paymentMethodsCard}
        elevation={2}
        variant="outlined"
      >
        <CardContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Payment Methods Array */}
            {paymentMethodFields.map((paymentField, paymentIndex) => (
              <div key={paymentField.id} className={classes.arrayItem}>
                <div className={classes.cardHeader}>
                  <div className={classes.cardTitle}>
                    <Chip
                      label={t('payments.plan_picker.payment_method_index', { index: paymentIndex + 1 })}
                      color="primary"
                      variant="filled"
                    />
                  </div>
                  <IconButton size="small" onClick={() => removePaymentMethod(paymentIndex)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </div>

                <div className={classes.formRow}>
                  <LokaliseField
                    name={`pricing.${pricingIndex}.layout.${tierIndex}.layout.layout.${paymentIndex}.title`}
                    control={control}
                    label={t('payments.plan_picker.payment_methods_title')}
                    dataTestId={`payment-method-title-${pricingIndex}-${tierIndex}-${paymentIndex}`}
                    required
                  />
                  <Controller
                    control={control}
                    name={`pricing.${pricingIndex}.layout.${tierIndex}.layout.layout.${paymentIndex}.type`}
                    rules={{ required: t('general.field_is_required') }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <TextField
                        required
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        label={t('payments.plan_picker.payment_method_type')}
                        placeholder={t('payments.plan_picker.payment_method_type_placeholder')}
                        className={classes.paymentMethodTypePicker}
                        error={!!error}
                      />
                    )}
                  />
                </div>
                <Controller
                  control={control}
                  name={`pricing.${pricingIndex}.layout.${tierIndex}.layout.layout.${paymentIndex}.icon`}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <AssetBrowser
                      assetType={AssetTypesEnum.PLAN_PICKER_PAYMENT_METHOD_ICON}
                      value={value}
                      onChange={onChange}
                      label={t('payments.plan_picker.payment_method_icon')}
                      hasError={!!error}
                    />
                  )}
                />
              </div>
            ))}

            <Button
              startIcon={<Add />}
              onClick={addPaymentMethod}
              className={classes.addButton}
              variant="contained"
              size="small"
            >
              {t('payments.plan_picker.add_payment_method')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
